import { z } from "zod";

import { storedEffortLevelOf } from "../schemas/ModelConfig/resolve-effort-level";
import { ALL_EFFORT_LEVELS, type ModelCatalogEntry } from "./model-catalog.config";
import {
  ALL_DIALECT_EFFORT_LEVELS,
  ANTHROPIC_THINKING_DIALECTS,
  type AnthropicThinkingDisplay,
  type AnyDialectEffortLevel,
  type DefaultEffort,
  DefaultEffortSchema,
  EFFORT_LADDER,
  type EffortOffForm,
  EffortRungSchema,
  encodeTuning,
  getDialectLevels,
  type ResolvedEffort,
  type ResolvedTuning,
  rungOf,
  type TemperatureRule,
  TemperatureRuleSchema,
  type ThinkingDialect
} from "./thinking-dialect.registry";

/** The one field a stored level sits in. */
export type EffortField = "reasoningLevel";

export type StoredTuning = {
  readonly reasoningLevel?: string | null;
  readonly thinkingDisplay?: AnthropicThinkingDisplay | null;
  readonly temperature?: number | null;
};

/**
 * A single level as the stored tuning a resolver reads. Always the canonical
 * `reasoningLevel`; `dialect` still gates it, because a model with no dialect
 * has no level to carry.
 */
export function storedLevelOf(
  dialect: ThinkingDialect | null,
  word: string | undefined
): StoredTuning | undefined {
  if (dialect === null || word === undefined) return undefined;
  return { reasoningLevel: word };
}

/**
 * The level a stored tuning carries, and the field it came from — always
 * `reasoningLevel`. One reading, shared by the resolver here and every write policy
 * that compares a stored level with a written one.
 */
export function storedEffortOf(
  stored: StoredTuning
): { readonly field: EffortField; readonly word: string } | undefined {
  const word = storedEffortLevelOf(stored)?.toLowerCase();
  return word === undefined ? undefined : { field: "reasoningLevel", word };
}

export interface ModelControlsFacts {
  readonly defaultEffort: DefaultEffort;
  readonly effortOff?: EffortOffForm;
  readonly temperatureRule: TemperatureRule;
}

export type CatalogTuningEntry = Pick<
  ModelCatalogEntry,
  | "provider"
  | "thinkingDialect"
  | "supportedReasoningLevels"
  | "defaultEffort"
  | "effortOff"
  | "temperatureRule"
> & {
  readonly offFormDeclared?: false;
};

export interface EndpointTuningDeclaration {
  readonly thinkingDialect: ThinkingDialect | null;
}

export type TuningEntry = CatalogTuningEntry | EndpointTuningDeclaration;

interface TuningFacts extends ModelControlsFacts {
  readonly offFormDeclared: boolean;
  readonly dialect: ThinkingDialect | null;
  readonly offered: readonly AnyDialectEffortLevel[];
}

const isEffortLevel = (word: string): word is AnyDialectEffortLevel =>
  (ALL_DIALECT_EFFORT_LEVELS as readonly string[]).includes(word);

const isCatalogTuningEntry = (entry: TuningEntry): entry is CatalogTuningEntry =>
  "provider" in entry;

function tuningFactsOf(entry: TuningEntry): TuningFacts {
  const dialect = entry.thinkingDialect ?? null;
  const dialectVocabulary = getDialectLevels(dialect).filter(isEffortLevel);

  if (!isCatalogTuningEntry(entry)) {
    return {
      dialect,
      offFormDeclared: false,
      offered: dialectVocabulary,
      defaultEffort: "vendor-default",
      temperatureRule: "never"
    };
  }

  const offered = dialect ? (entry.supportedReasoningLevels ?? dialectVocabulary) : [];
  const { defaultEffort, effortOff, temperatureRule } = entry;
  return {
    dialect,
    offFormDeclared: entry.offFormDeclared !== false,
    offered,
    defaultEffort,
    temperatureRule,
    ...(effortOff !== undefined && { effortOff })
  };
}

export const REPLACED_REASONS = ["not-offered", "no-off-form"] as const;
export type ReplacedReason = (typeof REPLACED_REASONS)[number];

export interface ReplacedTuningValue {
  readonly field: EffortField;
  readonly value: string;
  readonly reason: ReplacedReason;
}

export interface ResolveModelTuningInput {
  readonly stored: StoredTuning;
  readonly entry: TuningEntry;
  readonly defaultTemperature?: number;
}

export interface ResolvedModelTuning {
  readonly tuning: ResolvedTuning;
  readonly replaced: readonly ReplacedTuningValue[];
}

export const STORED_NONE = "none";

function defaultEffortOf(facts: TuningFacts): ResolvedEffort {
  const { defaultEffort } = facts;
  switch (defaultEffort) {
    case "vendor-default":
      return { kind: "vendor-default" };
    case "none":
      return facts.effortOff === undefined
        ? { kind: "vendor-default" }
        : { kind: "off", form: facts.effortOff };
    default: {
      const word = facts.offered.find((level) => rungOf(level) === defaultEffort);
      return word === undefined ? { kind: "vendor-default" } : { kind: "level", rung: word };
    }
  }
}

function offeredWordFor(facts: TuningFacts, storedWord: string): AnyDialectEffortLevel | undefined {
  const own = facts.offered.find((level) => level === storedWord);
  if (own !== undefined) return own;
  if (!isEffortLevel(storedWord)) return undefined;
  const storedRung = rungOf(storedWord);
  return facts.offered.find((level) => rungOf(level) === storedRung);
}

function resolveEffort(
  facts: TuningFacts,
  stored: StoredTuning
): { readonly effort: ResolvedEffort; readonly replaced: readonly ReplacedTuningValue[] } {
  if (facts.dialect === null) return { effort: defaultEffortOf(facts), replaced: [] };

  const read = storedEffortOf(stored);
  if (read === undefined) return { effort: defaultEffortOf(facts), replaced: [] };
  const { field, word: storedWord } = read;

  if (storedWord === STORED_NONE) {
    return facts.effortOff === undefined
      ? {
          effort: defaultEffortOf(facts),
          replaced: [{ field, value: storedWord, reason: "no-off-form" }]
        }
      : { effort: { kind: "off", form: facts.effortOff }, replaced: [] };
  }

  const word = offeredWordFor(facts, storedWord);
  return word === undefined
    ? {
        effort: defaultEffortOf(facts),
        replaced: [{ field, value: storedWord, reason: "not-offered" }]
      }
    : { effort: { kind: "level", rung: word }, replaced: [] };
}

function isTemperatureAdmitted(rule: TemperatureRule, effort: ResolvedEffort): boolean {
  switch (rule) {
    case "always":
      return true;
    case "when-off":
      return effort.kind !== "level";
    case "never":
      return false;
  }
}

const hasDisplay = (dialect: ThinkingDialect | null): boolean =>
  (ANTHROPIC_THINKING_DIALECTS as readonly (ThinkingDialect | null)[]).includes(dialect);

export function resolveModelTuning(input: ResolveModelTuningInput): ResolvedModelTuning {
  const facts = tuningFactsOf(input.entry);
  const { effort, replaced } = resolveEffort(facts, input.stored);
  const display = input.stored.thinkingDisplay ?? undefined;
  const temperature = input.stored.temperature ?? input.defaultTemperature;

  return {
    tuning: {
      effort,
      ...(display !== undefined && hasDisplay(facts.dialect) && { display }),
      ...(temperature !== undefined &&
        isTemperatureAdmitted(facts.temperatureRule, effort) && { temperature })
    },
    replaced
  };
}

export const ModelControlsSchema = z.object({
  levels: z.array(z.object({ rung: EffortRungSchema, stored: z.enum(ALL_EFFORT_LEVELS) })),
  offersNone: z.boolean(),
  defaultEffort: DefaultEffortSchema,
  effortWhenUnset: DefaultEffortSchema,
  temperature: TemperatureRuleSchema,
  offersDisplay: z.boolean(),
  offFormDeclared: z.boolean().default(true)
});
export type ModelControls = z.infer<typeof ModelControlsSchema>;

function isStructurallyEqual(left: unknown, right: unknown): boolean {
  if (left === right) return true;
  if (typeof left !== "object" || typeof right !== "object" || left === null || right === null) {
    return false;
  }
  const leftKeys = Object.keys(left);
  return (
    leftKeys.length === Object.keys(right).length &&
    leftKeys.every(
      (key) => key in right && isStructurallyEqual(Reflect.get(left, key), Reflect.get(right, key))
    )
  );
}

function isVendorDefaultTheOffForm(facts: TuningFacts): boolean {
  const { dialect, effortOff } = facts;
  if (dialect === null || effortOff === undefined) return false;
  return isStructurallyEqual(
    encodeTuning(dialect, { effort: { kind: "vendor-default" } }),
    encodeTuning(dialect, { effort: { kind: "off", form: effortOff } })
  );
}

function effortWhenUnsetOf(facts: TuningFacts): DefaultEffort {
  const effort = defaultEffortOf(facts);
  switch (effort.kind) {
    case "level":
      return rungOf(effort.rung);
    case "off":
      return STORED_NONE;
    case "vendor-default":
      return isVendorDefaultTheOffForm(facts) ? STORED_NONE : "vendor-default";
  }
}

const ladderIndexOf = (level: AnyDialectEffortLevel): number =>
  EFFORT_LADDER.indexOf(rungOf(level));

export function modelControlsOf(entry: TuningEntry): ModelControls {
  const facts = tuningFactsOf(entry);
  return {
    levels: [...facts.offered]
      .sort((left, right) => ladderIndexOf(left) - ladderIndexOf(right))
      .map((level) => ({ rung: rungOf(level), stored: level })),
    offersNone: facts.effortOff !== undefined,
    defaultEffort: facts.defaultEffort,
    effortWhenUnset: effortWhenUnsetOf(facts),
    temperature: facts.temperatureRule,
    offersDisplay: hasDisplay(facts.dialect),
    offFormDeclared: facts.offFormDeclared
  };
}
